<template>
  <div class="oring-design-space">
    <div class="card">
      <div class="card__content">
        <div class="card__title">
          <h3 class="h4 card__title-text">
            {{ i18n.tv('Frontend.Title.Dimensions', 'ORingConfiguratorImport') }}
          </h3>
        </div>
        <div class="card__description">
          {{ i18n.tv('designSpaceDimensionsSubTitle', 'ORingConfigurator') }}
        </div>
        <div class="card__input-search">
          <div class="search-container">
            <AutoComplete
              v-model="oringDesignSpaceSku"
              :data-items="searchResults"
              class="item-number-input use-loader autocomplete-fixed-width"
              :loading="loading"
              @change="handleSearchChange"
              @select="handleProductSelect"
              :list-no-data-render="'emptyTemplate'"
              autocomplete="off"
            >
              <template #emptyTemplate>
                <span />
              </template>
            </AutoComplete>
          </div>
        </div>
        <div class="card__separator">
          <span class="separator__text">{{ i18n.tv('Frontend.Text.Or', 'ORingConfiguratorImport') }}</span>
        </div>
        <div class="card__lower-half">
          <div class="card__lower-half__input">
            <div class="input-container">
              <NumericInput
                v-model="oringDesignSpaceD1"
                :label="getLabelWithUnit(i18n.tv('Frontend.Ring.D1', 'ORingConfiguratorImport'))"
                :placeholder="' '"
                :manual-errors="errorMessageD1"
                :decimals="decimalPlaces"
                :step="stepValue"
                :min="0"
                :format="formatString"
                class="item-number-input"
                @update:model-value="validate"
              />
              <NumericInput
                v-model="oringDesignSpaceD2"
                :label="getLabelWithUnit(i18n.tv('Frontend.Ring.D2', 'ORingConfiguratorImport'))"
                :placeholder="' '"
                :manual-errors="errorMessageD2"
                :decimals="decimalPlaces"
                :step="stepValue"
                :min="0"
                :format="formatString"
                class="item-number-input"
                @update:model-value="validate"
              />
            </div>
            <div class="text-container">
              <ActionInfoFilled class="info-icon" />
              <span>
                {{ i18n.tv('Frontend.Info.FreeDimensionsOne', 'ORingConfiguratorImport') }}
              </span>
            </div>
          </div>
          <div class="card__lower-half__image">
            <img loading="lazy"
              :src="oRingDimensionsImg"
              alt="O-Ring Dimenstions D1 and D2 Image"
              class="oring-design-space-image"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref} from 'vue'
import NumericInput from '~/components/input-components/NumericInput.vue'
import AutoComplete from '~/components/input-components/Autocomplete.vue'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import ActionInfoFilled from '~/assets/icons/action_info_filled.svg?component'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {storeToRefs} from 'pinia'
import oRingDimensionsImg from '~/assets/images/O-Ring_Dimensions.png'
import useI18nExtended from '~/composables/usei18nExtended'

const i18n = useI18nExtended()
const oRingCalculatorStore = useORingCalculatorStore()

const {
  currentLanguage,
  oringCalculatorImperial,
  oringDesignSpaceD1,
  oringDesignSpaceD2,
  oringDesignSpaceSku
} = storeToRefs(oRingCalculatorStore)

const {
  productSearchAutoCompleteORingConfigurator,
  getProductDetailsORingConfigurator,
  checkRingORingConfigurator
} = useOringCalculatorService()

const errorMessageD1 = ref('')
const errorMessageD2 = ref('')
const loading = ref(false)
const searchResults = ref<string[]>([])
const productDetails = ref<any>(null)
let timeout: any = null

const isValid = computed(() => {
  return oringDesignSpaceD1.value !== null
    && oringDesignSpaceD2.value !== null
    && !errorMessageD1.value
    && !errorMessageD2.value
})

const validate = async () => {
  const d1Value = oringDesignSpaceD1.value !== null ? Number(oringDesignSpaceD1.value) : -1
  const d2Value = oringDesignSpaceD2.value !== null ? Number(oringDesignSpaceD2.value) : -1

  const validationResponse = await checkRingORingConfigurator({
    d1raw: d1Value,
    d2raw: d2Value,
    language: currentLanguage.value,
    imperial: oringCalculatorImperial.value
  })

  if (validationResponse.errorDTO && validationResponse.errorDTO.error) {
    for (const error of validationResponse.errorDTO.errors) {
      if (error.elementName === 'd1') {
        errorMessageD1.value = `${error.errorMessage} ${error.thresholdValue} ${oringCalculatorImperial.value ? 'inch' : 'mm'}`
      } else if (error.elementName === 'd2') {
        errorMessageD2.value = `${error.errorMessage} ${error.thresholdValue} ${oringCalculatorImperial.value ? 'inch' : 'mm'}`
      }
    }

    return
  }

  errorMessageD1.value = ''
  errorMessageD2.value = ''
}

const getLabelWithUnit = (label: string) => {
  return `${label} [${oringCalculatorImperial.value ? 'inch' : 'mm'}]`
}

// Number formatting properties to match ToleranceInputRow
const decimalPlaces = computed(() => {
  return oringCalculatorImperial.value ? 4 : 3
})

const stepValue = computed(() => {
  return oringCalculatorImperial.value ? 0.01 : 0.1
})

const formatString = computed(() => {
  return oringCalculatorImperial.value ? 'n4' : 'n3'
})

const handleSearchChange = async (event: any) => {
  if (timeout) {
    clearTimeout(timeout)
  }

  const value = event?.target?.value || ''
  if (value && value.trim().length > 0) {
    timeout = setTimeout(async () => {
      loading.value = true
      try {
        const results = await fetchSearchResults(value.trim())
        searchResults.value = results
      } catch (error) {
        searchResults.value = []
      } finally {
        loading.value = false
      }
    }, 350)
  } else {
    searchResults.value = []
  }
}

const fetchSearchResults = async (query: string): Promise<string[]> => {
  try {
    const response = await productSearchAutoCompleteORingConfigurator({
      searchTermProductId: query
    })

    if (response && Array.isArray(response.data)) {
      return response.data.map((item: any) => `${item.productId}`)
    }
    return []
  } catch (error) {
    return []
  }
}

const handleProductSelect = async (event: any) => {
  if (!event || !event.target || !event.target.value) {return}

  const selectedProductId = event.target.value
  loading.value = true

  try {
    const productData = await getProductDetails(selectedProductId)
    if (productData?.details) {
      oringDesignSpaceD1.value = productData.details.d1
      oringDesignSpaceD2.value = productData.details.d2

      productDetails.value = productData.details
    }
    await validate()
  } catch (error) {
    loading.value = false
  } finally {
    loading.value = false
  }
}

const getProductDetails = async (productId: number) => {
  try {
    const response = await getProductDetailsORingConfigurator({
      productId: productId,
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value
    })

    if (response && response.succeeded) {
      return response.data
    }
    return null
  } catch (error) {
    return null
  }
}

defineExpose({
  isValid
})
</script>

<style lang="scss">
.oring-design-space {
  margin-bottom: 1rem;

  .card {
    padding: 1rem;
    box-shadow: $shadow-default;
    margin: 1.25rem 0;

    &__title {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding-bottom: 1rem;
      margin: 0;

      &-text {
        margin: 0;
        font-weight: $base-text-font-weight-semi-bold;
        line-height: 1.5rem;
        font-size: 1.125rem;
      }
    }

    &__description {
      padding-bottom: 1rem;
      font-weight: $base-text-font-weight-light;
    }

    &__input-search {
      display: flex;
      flex-direction: column;
      position: relative;

      .search-container {
        display: flex;
        align-items: stretch;
      }

      .item-number-input {
        min-width: 11.688rem;
        margin-bottom: 0;
      }

      .autocomplete-fixed-width {
        &.k-autocomplete,
        .k-autocomplete {
          .k-clear-value {
            position: absolute;
            right: 0.1rem;
            top: 50%;
            transform: translateY(-50%);
            z-index: 5;

            &:hover {
              background: transparent;
            }
          }

					input {
						width: 100%;
					}
        }
      }
    }

    &__separator {
      position: relative;
      text-align: left;
      margin: 1rem 0;
      padding-left: 2rem;

      &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: 0.0625rem;
        background-color: $color__fst__dark__gray;
        z-index: 1;
      }

      .separator__text {
        position: relative;
        background-color: $color__white;
        padding: 0 0.75rem;
        z-index: 2;
      }
    }

    &__lower-half {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 2rem;

      @include breakpoint-down($lg) {
        flex-direction: column;
      }

      &__input {
        max-width: 24.5rem;
        flex: 0 0 auto;

        @include breakpoint-down($md) {
          max-width: 100%;
          width: 100%;
          margin-bottom: 2rem;
        }

        .input-container {
					display: flex;
          gap: 1rem;

          @include breakpoint-down($md) {
            flex-direction: column;
          }

          .item-number-input {
            min-width: 11.688rem;

            .k-floating-label-container {
              padding-top: 1.625rem;
              width: 100%;

							input {
								font-weight: $base-text-font-weight-light;
							}
            }

            label {
              font-weight: $base-text-font-weight-light;
              margin-bottom: .5rem;
              overflow: hidden;
							max-width: 100%;
            }
          }
        }

        .text-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1rem;
          padding: 0.5rem;
          border: 0.0625rem solid $color__black;
          width: 100%;
          box-sizing: border-box;

          .info-icon {
            width: 1.25rem;
            height: 1.25rem;
            flex-shrink: 0;
            fill: $color__fst__primary;
          }

          span {
            flex: 1;
            font-weight: $base-text-font-weight-light;
          }
        }
      }

      &__image {
        margin-left: 0;
        width: auto;
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;

        @include breakpoint-down($md) {
          width: 100%;
          margin-top: 1rem;
        }

        .oring-design-space-image {
          max-width: 100%;
          max-height: 10rem;
          object-fit: contain;
        }
      }
    }
  }
}
</style>
